/*  ========================================================================
    # Component - Application Form - Upload Input
    ========================================================================  */

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '../../hooks/useTranslation';
import useSVGSprite from '../../hooks/useSVGSprite';

const ALLOWED_FILE_TYPES = ['pdf', 'jpg', 'jpeg', 'gif', 'png', 'docx', 'txt'];
const ALLOWED_FILE_EXTENSIONS = ALLOWED_FILE_TYPES.map(type => `.${type}`).join(',');
const MAX_FILE_SIZE = 18 * 1024 * 1024;

const shortenFileName = (fileName, maxLength = 30) => {
  if (fileName.length <= maxLength) return fileName;
  const extension = fileName.substring(fileName.lastIndexOf('.'));
  const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
  const charsToShow = maxLength - extension.length - 3;
  return nameWithoutExt.substring(0, charsToShow) + '...' + extension;
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / k ** i) * 100) / 100 + ' ' + sizes[i];
};

const UploadInput = ({
  label,
  name,
  required,
  onFileChange,
  otherFilesSize,
  customFileSizeError
}) => {
  const __ = useTranslation();
  const sprite = useSVGSprite();
  const [slots, setSlots] = useState([]);
  const [error, setError] = useState(null);
  const inputRefs = useRef({});
  const nextId = useRef(0);
  const pendingClickId = useRef(null);

  const hasFiles = slots.some(s => s.file);
  const totalOwnSize = slots.reduce((sum, s) => sum + (s.file ? s.file.size : 0), 0);

  // Trigger click on a newly created slot's input after render
  useEffect(() => {
    if (pendingClickId.current !== null) {
      const id = pendingClickId.current;
      pendingClickId.current = null;
      if (inputRefs.current[id]) {
        inputRefs.current[id].click();
      }
    }
  }, [slots]);

  const handleAddClick = () => {
    const id = nextId.current++;
    pendingClickId.current = id;
    setSlots(prev => [...prev, { id, file: null }]);
  };

  const handleFileChange = (slotId, e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      // User cancelled the dialog — remove the empty slot
      setSlots(prev => prev.filter(s => s.id !== slotId));
      return;
    }

    const newTotalSize = selectedFile.size + (otherFilesSize || 0) + totalOwnSize;
    if (newTotalSize > MAX_FILE_SIZE) {
      let errorMessage = null;

      if (customFileSizeError) {
        errorMessage = customFileSizeError;
      } else {
        errorMessage = __('form-application-message-file-too-large');
      }

      errorMessage = errorMessage.replace('{maxSize}', formatFileSize(MAX_FILE_SIZE));
      errorMessage = errorMessage.replace('{currentSize}', formatFileSize(selectedFile.size));
      errorMessage = errorMessage.replace('{fileName}', selectedFile.name);

      setError(errorMessage);

      e.target.value = '';
      setSlots(prev => prev.filter(s => s.id !== slotId));
      return;
    }

    setError(null);
    setSlots(prev => prev.map(s => (s.id === slotId ? { ...s, file: selectedFile } : s)));

    // Compute updated file list from current slots state (slotId not yet updated in state)
    const updatedFiles = slots.map(s => (s.id === slotId ? selectedFile : s.file)).filter(Boolean);
    if (onFileChange) onFileChange(updatedFiles);
  };

  const handleRemove = (slotId, e) => {
    e.stopPropagation();

    if (inputRefs.current[slotId]) {
      inputRefs.current[slotId].value = '';
    }

    setSlots(prev => prev.filter(s => s.id !== slotId));
    setError(null);

    const remainingFiles = slots.filter(s => s.id !== slotId && s.file).map(s => s.file);
    if (onFileChange) onFileChange(remainingFiles);
  };

  return (
    <fieldset className="c-form__item c-form__item--third c-form__item--download">
      {/* Proxy input for required validation — hidden but not display:none so browsers validate it */}
      {required && (
        <input
          type="text"
          required
          value={hasFiles ? 'selected' : ''}
          tabIndex={-1}
          aria-hidden="true"
          style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
        />
      )}

      {/* One hidden file input per slot — stays in DOM so FormData picks up the selected file */}
      {slots.map(slot => (
        <input
          key={slot.id}
          type="file"
          name={`${name}[]`}
          accept={ALLOWED_FILE_EXTENSIONS}
          ref={el => {
            inputRefs.current[slot.id] = el;
          }}
          onChange={e => handleFileChange(slot.id, e)}
          style={{ display: 'none' }}
        />
      ))}

      {/* Container with white background — holds chips and the add-button label */}
      <div className="c-form__upload-area" onClick={handleAddClick}>
        {slots
          .filter(s => s.file)
          .map(slot => (
            <span
              key={slot.id}
              className="c-form__file-name"
              onClick={e => handleRemove(slot.id, e)}
            >
              <svg viewBox="0 0 20 20">
                <use href={`${sprite}#trash`} />
              </svg>
              {shortenFileName(slot.file.name)}
            </span>
          ))}
        <label>{label}</label>
      </div>

      {required && !hasFiles && (
        <span className="c-form__required-message">{__('form-required-message')}</span>
      )}
      {error && <p className="c-form__message c-form__message--error">{error}</p>}
    </fieldset>
  );
};

UploadInput.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  required: PropTypes.bool,
  onFileChange: PropTypes.func,
  otherFilesSize: PropTypes.number,
  customFileSizeError: PropTypes.string
};

export default UploadInput;
